import { isDefined, isNotDefined } from '@sgme/fp';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { getCurrenciesFromCurrencyPair, getRfsMarginData } from '@/App/tiles/FlexiForward/utils';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { useCheckFlexiForwardByTradeCaptureMutation } from '@/store/flexi-forward/api';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export function CommercialMarginAmountInput({ tileId }: { tileId: string }): ReactElement {
  const { locale } = useIntl();

  const [checkFlexiForward] = useCheckFlexiForwardByTradeCaptureMutation();

  const { currency1, currency2, marginCcy1, marginCcy2, marginCurrency, notional, tradeCapture } =
    useFlexiForwardSelector((flexiForward) => {
      const { properties, tradeCapture } = flexiForward;

      const { currency1, currency2 } = getCurrenciesFromCurrencyPair(properties.currencyPair);

      const marginData = getRfsMarginData(properties.rfs);

      return {
        currency1,
        currency2,
        marginCcy1: marginData?.markupCcy1,
        marginCcy2: marginData?.markupCcy2,
        marginCurrency: properties.marginCurrency,
        notional: properties.notional ?? undefined,
        tradeCapture,
      };
    });

  const marginValue = marginCurrency === currency1 ? marginCcy1 : marginCcy2;
  const marginAmount = isDefined(marginValue) ? Number.parseFloat(marginValue) : undefined;

  const handleMarginCurrencyClicked = () => {
    if (isNotDefined(tradeCapture)) {
      return;
    }

    if (isNotDefined(notional) || isNotDefined(currency1) || isNotDefined(currency2)) {
      return;
    }

    const currencies: { marginCurrency: string; notionalCurrencyIndex: CurrencyIndex } =
      marginCurrency === currency1
        ? { marginCurrency: currency2, notionalCurrencyIndex: 2 }
        : { marginCurrency: currency1, notionalCurrencyIndex: 1 };

    checkFlexiForward({
      tileId,
      tradeCapture,
      properties: {
        ...currencies,
        notional,
      },
    });
  };

  return (
    <div className="d-flex">
      <SgNumberInput locale={locale} value={marginAmount} data-e2e="marginAmountInput" disabled />
      <button
        tabIndex={-1}
        type="button"
        data-e2e="marginAmount-btn"
        onClick={handleMarginCurrencyClicked}
        className="px-1 btn btn-discreet-primary btn-sm"
      >
        {marginCurrency}
      </button>
    </div>
  );
}
